import React, { useEffect, useState } from 'react';
import { BOTTOM_INDENTS, COLORS, TOP_INDENTS, validateDebounce, VALIDATION_RULES } from '@constants';
import { ACCOUNT_USER_ROLE, ACCOUNT_USER_STATUS } from '@enums';
import { useModal, useStore } from '@hooks';
import { Button, Input, Select, Tag, Typography } from '@ui';
import { Col, Flex, Form, Row, Spin } from 'antd';
import { observer } from 'mobx-react';
import { dayjs } from '../../../../../../../../utils/dayjs';
import { ACCOUNT_USER_ROLE_LABELS, ACCOUNT_USER_STATUS_LABELS } from '../../constants';
import { getFullName } from '../../utils';
import DeleteModal from './components/DeleteModal';
import { StyledCard } from './styled';

const TeamMember = observer(({ isDisabled = false, onClick, member, isOwner = false, isSelect = false, isActive }) => {
    const [form] = Form.useForm();
    const { open, close } = useModal();
    const store = useStore();
    const { handleTeamMemberUpdate, handleCancelOwnership, handleTeamMemberResendInvite } = store.accountsStore;

    const [loading, setLoading] = useState(false);
    const [edit, setEdit] = useState(false);

    const fullName = getFullName(member.user?.firstName, member.user?.lastName);

    useEffect(() => {
        form.setFieldsValue(member.user);
    }, [member]);

    const handleUpdateMember = async () => {
        setLoading(true);
        try {
            const values = form.getFieldsValue();
            await handleTeamMemberUpdate({ ...values, id: member.id });
        } finally {
            setEdit(false);
            setLoading(false);
        }
    };

    const handleResendInvite = async () => {
        setLoading(true);
        try {
            await handleTeamMemberResendInvite(member);
        } finally {
            setEdit(false);
            setLoading(false);
        }
    };

    const onCancelTransfer = async () => {
        setLoading(true);
        try {
            await handleCancelOwnership(member);
        } finally {
            setEdit(false);
            setLoading(false);
        }
    };

    const onDelete = () => {
        open({
            title: <span data-e2e='team-management_delete-modal_title'>Delete staff account?</span>,
            content: <DeleteModal member={member} fullName={fullName} close={close} />,
            settings: {
                width: '630px'
            }
        });
    };

    const onSelect = () => {
        if (typeof onClick === 'function') onClick(member);
    };

    return (
        <StyledCard
            $disabled={isDisabled}
            $edit={edit}
            $isActive={isActive}
            $hover={isSelect}
            onClick={onSelect}
            data-e2e={`team-management_member-${member.role}_card`}
        >
            <Spin spinning={loading}>
                <Flex justify='space-between' align='center' gap={24}>
                    <Flex vertical gap={16} justify='space-between'>
                        <Typography.Text type='badgeMedium' data-e2e='team-management_member-full-name_value'>
                            {fullName}
                        </Typography.Text>
                        <Typography.Text
                            type='body2'
                            color={COLORS.$gray60Black}
                            style={{ alignSelf: 'end' }}
                            data-e2e='team-management_member-last-login_value'
                        >
                            Last login was {dayjs(member.lastLoginAt).format('dddd, MMMM D, YYYY, h:mm a')}
                        </Typography.Text>
                    </Flex>
                    {isOwner ? null : (
                        <Flex vertical gap={16}>
                            <Flex gap={16}>
                                {isSelect
                                    ? null
                                    : !edit && (
                                          <Button
                                              type='link'
                                              onClick={() => setEdit(true)}
                                              fontSize='14px'
                                              padding={0}
                                              data-e2e='team-management_edit-member_btn'
                                          >
                                              Edit
                                          </Button>
                                      )}

                                {isSelect ? null : (
                                    <Button
                                        type='link'
                                        onClick={onDelete}
                                        fontSize='14px'
                                        padding={0}
                                        data-e2e='team-management_delete-member_btn'
                                    >
                                        Delete
                                    </Button>
                                )}

                                {member.status === ACCOUNT_USER_STATUS.TRANSFER_OWNERSHIP && !isSelect ? (
                                    <Button
                                        type='link'
                                        onClick={onCancelTransfer}
                                        fontSize='14px'
                                        padding={0}
                                        data-e2e='team-management_cancel-ownership-transfer_btn'
                                    >
                                        Cancel Ownership Transfer
                                    </Button>
                                ) : null}

                                <Tag
                                    color={ACCOUNT_USER_STATUS_LABELS[member.status]?.color ?? COLORS.$gray40Black}
                                    data-e2e='team-management_member-status_value'
                                >
                                    {ACCOUNT_USER_STATUS_LABELS[member.status]?.title ?? 'unknown'}
                                </Tag>
                            </Flex>
                            <Flex justify='end'>
                                <Typography.Text type='badgeMedium' data-e2e='team-management_member-role_value'>
                                    {ACCOUNT_USER_ROLE_LABELS[member.role]?.title ?? 'unknown'}
                                </Typography.Text>
                            </Flex>
                        </Flex>
                    )}
                </Flex>
                {!isOwner && edit ? (
                    <Form
                        data-e2e='team-management_edit-member_form'
                        initialValues={{
                            email: member.user.email,
                            firstName: member.user.firstName,
                            lastName: member.user.lastName,
                            role: member.role
                        }}
                        style={TOP_INDENTS.M}
                        preserve={false}
                        form={form}
                    >
                        <Row gutter={[16, 16]} style={BOTTOM_INDENTS.M}>
                            <Col span={12}>
                                <Form.Item
                                    name='firstName'
                                    onBlur={(e) => form.setFieldValue('firstName', e.target.value.trim())}
                                >
                                    <Input label='First Name' data-e2e='team-management_first-name_input' />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name='lastName'
                                    onBlur={(e) => form.setFieldValue('lastName', e.target.value.trim())}
                                >
                                    <Input label='Last Name' data-e2e='team-management_last-name_input' />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name='email'
                                    rules={[...VALIDATION_RULES.REQUIRED, ...VALIDATION_RULES.EMAIL]}
                                    validateDebounce={validateDebounce}
                                    normalize={(value) => value?.trim()}
                                >
                                    <Input disabled label='Email address' data-e2e='team-management_email_input' />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item name='role'>
                                    <Select
                                        data-e2e='team-management_select-access_dropdown'
                                        label='Select Access'
                                        options={[
                                            {
                                                value: ACCOUNT_USER_ROLE.MEMBER,
                                                label: (
                                                    <span data-e2e='team-management_member_option'>
                                                        {ACCOUNT_USER_ROLE_LABELS[ACCOUNT_USER_ROLE.MEMBER].title}
                                                    </span>
                                                )
                                            },
                                            {
                                                value: ACCOUNT_USER_ROLE.ADMIN,
                                                label: (
                                                    <span data-e2e='team-management_admin_option'>
                                                        {ACCOUNT_USER_ROLE_LABELS[ACCOUNT_USER_ROLE.ADMIN].title}
                                                    </span>
                                                )
                                            },
                                            {
                                                value: ACCOUNT_USER_ROLE.DEVELOPER,
                                                label: (
                                                    <span data-e2e='team-management_developer_option'>
                                                        {ACCOUNT_USER_ROLE_LABELS[ACCOUNT_USER_ROLE.DEVELOPER].title}
                                                    </span>
                                                )
                                            }
                                        ]}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Flex justify='end'>
                            <Flex gap={16}>
                                <Button
                                    onClick={() => setEdit(false)}
                                    style={{ minWidth: 160 }}
                                    data-e2e='team-management_cancel-editing_btn'
                                >
                                    Cancel
                                </Button>
                                {member.status === ACCOUNT_USER_STATUS.INVITED ? (
                                    <Button
                                        onClick={handleResendInvite}
                                        style={{ minWidth: 160 }}
                                        data-e2e='team-management_resend-invite_btn'
                                    >
                                        Re-send Invite
                                    </Button>
                                ) : null}
                                <Button
                                    onClick={handleUpdateMember}
                                    htmlType='submit'
                                    style={{ minWidth: 160 }}
                                    type='primary'
                                    data-e2e='team-management_save-changes_btn'
                                >
                                    Save Changes
                                </Button>
                            </Flex>
                        </Flex>
                    </Form>
                ) : null}
            </Spin>
        </StyledCard>
    );
});

export default TeamMember;
